/**
 * The rules behind the sign-in, account and Team screens — JSX-free, so the dashboard's node-only
 * Jest can hold them.
 *
 * The password rules MIRROR the API's `checkPasswordPolicy` (`operator-password.ts`); the API stays
 * the authority and refuses anything these let through. Mirrored only so the operator learns
 * "too short" while typing rather than after a round trip — and with the same numbers, so the hint
 * and the refusal never disagree.
 */

/** Shortest acceptable password — the API's `MIN_PASSWORD_LENGTH` (NIST SP 800-63B rev. 4). */
export const MIN_PASSWORD_LENGTH = 15;

/** Longest acceptable password — the API's `MAX_PASSWORD_LENGTH`. */
export const MAX_PASSWORD_LENGTH = 128;

/** Why a proposed password cannot be sent yet. Names match the API's `violation` codes. */
export type PasswordProblem =
  | 'TOO_SHORT'
  | 'TOO_LONG'
  | 'CONTAINS_USERNAME'
  | 'SAME_AS_CURRENT'
  | 'MISMATCH';

/** The API's refusal codes this module turns into the operator's language. */
export const OPERATOR_LOCKED = 'OPERATOR_LOCKED';
export const OPERATOR_PASSWORD_POLICY = 'OPERATOR_PASSWORD_POLICY';

/** A new-password form as typed. */
export interface NewPasswordDraft {
  readonly username: string;
  /** The password being replaced; empty when unknown. */
  readonly current: string;
  readonly next: string;
  readonly confirm: string;
}

/**
 * The first problem with a proposed password, or null when it may be sent.
 *
 * Length is counted in code points after NFKC, as the API counts it.
 */
export function passwordProblem(
  draft: NewPasswordDraft,
): PasswordProblem | null {
  const next = draft.next.normalize('NFKC');
  const length = [...next].length;
  if (length < MIN_PASSWORD_LENGTH) return 'TOO_SHORT';
  if (length > MAX_PASSWORD_LENGTH) return 'TOO_LONG';
  const username = draft.username.trim().toLowerCase();
  if (username && next.toLowerCase().includes(username)) {
    return 'CONTAINS_USERNAME';
  }
  if (draft.current && next === draft.current.normalize('NFKC')) {
    return 'SAME_AS_CURRENT';
  }
  if (draft.next !== draft.confirm) return 'MISMATCH';
  return null;
}

/** The i18n key that explains a password problem. */
export function passwordProblemKey(problem: PasswordProblem): string {
  const keys: Record<PasswordProblem, string> = {
    TOO_SHORT: 'account.problems.tooShort',
    TOO_LONG: 'account.problems.tooLong',
    CONTAINS_USERNAME: 'account.problems.containsUsername',
    SAME_AS_CURRENT: 'account.problems.sameAsCurrent',
    MISMATCH: 'account.problems.mismatch',
  };
  return keys[problem];
}

function fieldOf(body: unknown, field: string): unknown {
  if (typeof body !== 'object' || body === null) return undefined;
  return Object.entries(body).find(([name]) => name === field)?.[1];
}

/**
 * Turns a refused sign-in or password change into an i18n key, when the API said which rule it
 * broke. Null means "show the API's own message" — a wrong password, for instance, which the API
 * words deliberately vaguely.
 *
 * @param body - The error response body.
 */
export function authRefusalKey(body: unknown): string | null {
  const code = fieldOf(body, 'code');
  if (code === OPERATOR_LOCKED) return 'login.locked';
  if (code === OPERATOR_PASSWORD_POLICY) {
    const violation = fieldOf(body, 'violation');
    if (
      violation === 'TOO_SHORT' ||
      violation === 'TOO_LONG' ||
      violation === 'CONTAINS_USERNAME' ||
      violation === 'SAME_AS_CURRENT'
    ) {
      return passwordProblemKey(violation);
    }
  }
  return null;
}
